import { useQueryClient } from '@tanstack/react-query';
import { isMarketType, type NotificationTab } from './notification-types';
import {
  getNotificationsListQueryKey,
  getNotificationsUnreadCountsQueryKey,
  useNotificationsMarkAllRead,
  useNotificationsMarkRead,
} from '@/lib/endpoints';
import type { NotificationListResponseDto, NotificationResponseDto, UnreadCountsResponseDto } from '@/lib/models';

export interface NotificationActions {
  /** Mark one notification read (optimistic: drops its dot + decrements the pills). */
  markRead: (notification: NotificationResponseDto) => void;
  /** Mark read, scoped to the active tab (optimistic: clears that tab's dots + pill). */
  markAllRead: (tab: NotificationTab) => void;
  isMarkingAll: boolean;
}

/** Which pill a type counts toward. */
function bucketOf(type: NotificationResponseDto['type']): 'markets' | 'account' {
  return isMarketType(type) ? 'markets' : 'account';
}

/** Is a row in scope for a tab-scoped mark-all? */
function inTab(notification: NotificationResponseDto, tab: NotificationTab): boolean {
  if (tab === 'ALL') return true;
  return bucketOf(notification.type) === (tab === 'MARKETS' ? 'markets' : 'account');
}

/**
 * Read-state mutations with optimistic cache updates, shared by every
 * notification surface. Server data stays in React Query; we patch the cached
 * list pages (across all tab/search keys) + the per-tab counts immediately so
 * the UI feels instant, then reconcile on settle. Keys come from the generated
 * Orval query-key helpers — `getNotificationsListQueryKey()` (no params) is the
 * `['/notifications']` prefix that matches every list variant.
 */
export function useNotificationActions(): NotificationActions {
  const queryClient = useQueryClient();
  const listsKey = getNotificationsListQueryKey();
  const countsKey = getNotificationsUnreadCountsQueryKey();

  const reconcile = () => {
    void queryClient.invalidateQueries({ queryKey: listsKey });
    void queryClient.invalidateQueries({ queryKey: countsKey });
  };

  const flipRead = (predicate: (n: NotificationResponseDto) => boolean) => {
    queryClient.setQueriesData<NotificationListResponseDto>({ queryKey: listsKey }, (old) =>
      old
        ? {
            ...old,
            items: old.items.map((n) =>
              predicate(n) && !n.read ? { ...n, read: true, readAt: new Date().toISOString() } : n,
            ),
          }
        : old,
    );
  };

  const markReadMutation = useNotificationsMarkRead({ mutation: { onSettled: reconcile } });
  const markAllMutation = useNotificationsMarkAllRead({ mutation: { onSettled: reconcile } });

  return {
    markRead: (notification: NotificationResponseDto) => {
      if (!notification.read) {
        flipRead((n) => n.id === notification.id);
        const bucket = bucketOf(notification.type);
        queryClient.setQueryData<UnreadCountsResponseDto>(countsKey, (old) =>
          old ? { ...old, all: Math.max(0, old.all - 1), [bucket]: Math.max(0, old[bucket] - 1) } : old,
        );
      }
      markReadMutation.mutate({ id: notification.id });
    },
    markAllRead: (tab: NotificationTab) => {
      flipRead((n) => inTab(n, tab));
      queryClient.setQueryData<UnreadCountsResponseDto>(countsKey, (old) => {
        if (!old) return old;
        if (tab === 'ALL') return { all: 0, markets: 0, account: 0 };
        const bucket = tab === 'MARKETS' ? 'markets' : 'account';
        return { ...old, all: Math.max(0, old.all - old[bucket]), [bucket]: 0 };
      });
      markAllMutation.mutate({ params: { tab } });
    },
    isMarkingAll: markAllMutation.isPending,
  };
}
